/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Checkbox, Divider, FormControlLabel } from '@material-ui/core';
import { Field, FieldProps, Form, FormikProps } from 'formik';
import { moneyMask, storeNumberMask } from 'utils/masks';
import { EmailType } from 'hooks/requests/emailDisclosure';
import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
  Trans,
} from 'hooks/useTranslate/useTranslate';
import PageContainer from 'components/PageContainer';
import ActionButtonContainer, {
  TextAction,
} from 'components/ActionButtonsContainer';
import Button from 'components/Button';
import { FormTextField } from 'components/TextField';
import EmailDisclosureButton from 'components/EmailDisclosure/EmailDisclosureButton';
import DisclosureBox from 'features/DisclosureBox';
import RestartLink from '../form/RestartLink';

import { composeStyles } from 'features/Theme';
import styles from './styles';
import Title from 'components/Title';
import ReferralCodeField from 'components/form/ReferralCodeField';
import useFieldCustomization from 'hooks/useFieldCustomization';
import withOptional from 'components/form/withOptional';
import { AnnualIncomeField, LoyaltyNumberField } from 'components/form';

import { FormValues } from './UnifiedCreditApplicationReview';
import BackToPrevious from 'components/unified/BackToPrevious';
import ReCAPTCHA from 'react-google-recaptcha';

type PropTypes = FormikProps<FormValues> & {
  emailAddress: string | null;
};

const UnifiedCreditApplicationReviewView: React.FC<PropTypes> = props => {
  const { emailAddress, isValid, isSubmitting, setFieldValue } = props;
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const { isRequired, isVisible } = useFieldCustomization();

  const siteKey = process.env.REACT_APP_CAPTCHA_SITE_KEY || '';

  const handleReCaptchaVerify = (token: string | null) => {
    setFieldValue('reCaptchaToken', token);
  };

  const handleReCaptchaExpired = () => {
    setFieldValue('reCaptchaToken', null);
    console.log('ReCaptcha expired');
  };

  const handleReCaptchaError = () => {
    setFieldValue('reCaptchaToken', null);
    console.log('ReCaptcha errored');
  };

  return (
    <Form noValidate>
      <PageContainer maxWidth="md">
        <div css={styles.headingContainer}>
          <Title>{tMd('unified.offerReview.creditApp.title')}</Title>
        </div>
        <div css={styles.body}>
          {tMdMultiline('unified.offerReview.creditApp.subtitle')}
        </div>
        <div css={styles.disclosure}>
          <DisclosureBox type="allInOne" />
        </div>
        <div css={styles.section}>
          <Field name="consent" type="checkbox">
            {(fieldProps: FieldProps) => (
              <FormControlLabel
                label={
                  <div css={styles.checkboxLabel}>
                    {tMdMultiline(
                      'unified.offerReview.creditApp.formLabel.consent',
                    )}
                  </div>
                }
                control={
                  <Checkbox id="accountAssureConsent" {...fieldProps.field} />
                }
              />
            )}
          </Field>
        </div>
        <div css={styles.section}>
          <EmailDisclosureButton
            email={emailAddress}
            sendEmail={email => Promise.resolve(email)}
            emailType={EmailType.Disclosure}
          />
        </div>
        <div css={styles.section}>
          <Divider />
        </div>
        <div css={composeStyles([styles.section, styles.annualIncome])}>
          {isVisible('annualIncome') && (
            <div>
              <div>
                {tMdMultiline('unified.offerReview.creditApp.annualIncome')}
              </div>
              <AnnualIncomeField />
            </div>
          )}
          <ReferralCodeField />
          {isVisible('intendedPurchaseAmount') && (
            <Field name="intendedPurchaseAmount">
              {(fieldProps: FieldProps) => (
                <FormTextField
                  id="creditAppIntendedPurchaseAmount"
                  fieldProps={fieldProps}
                  required={true}
                  label={'Intended Purchase Amount'}
                  mask={moneyMask}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                />
              )}
            </Field>
          )}
          {isVisible('storeNumber') && (
            <Field name="storeNumber">
              {(fieldProps: FieldProps) => (
                <FormTextField
                  id="creditAppStoreNumber"
                  fieldProps={fieldProps}
                  required={isRequired('storeNumber')}
                  mask={storeNumberMask}
                  label={withOptional(
                    t('formLabel.storeNumber'),
                    isRequired('storeNumber'),
                  )}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                />
              )}
            </Field>
          )}

          <LoyaltyNumberField />
        </div>

        <div css={styles.recaptchaMessage}>{tMd('recaptcha.message')}</div>
        <div css={styles.captchaSection}>
          <ReCAPTCHA
            data-testid="reCaptchaWidget"
            sitekey={siteKey}
            onChange={handleReCaptchaVerify}
            onExpired={handleReCaptchaExpired}
            onErrored={handleReCaptchaError}
          />
        </div>
        <ActionButtonContainer>
          <Button
            mobileFullWidth={true}
            type="submit"
            id="formSubmit"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            {tMd('unified.offerReview.creditApp.submitButton')}
          </Button>
          <BackToPrevious tMdKey="unified.offerReview.creditApp.backButton" />

          <TextAction>
            <div css={styles.restartText}>
              <Trans
                i18nKey="unified.offerReview.creditApp.restartMessage"
                components={[<RestartLink />]}
              />
            </div>
          </TextAction>
        </ActionButtonContainer>
      </PageContainer>
    </Form>
  );
};

export default UnifiedCreditApplicationReviewView;
